import { Component, OnInit, inject } from '@angular/core';
import { DatePipe, TitleCasePipe } from '@angular/common';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogTitle, MatDialogContent, MatDialogActions, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButton } from '@angular/material/button';
import { NgxMaskPipe } from 'ngx-mask';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { MessageDialogComponent } from '../message-dialog/message-dialog.component';
import { ThemeService } from '~/core/services/theme.service';
import { ApiErrorParser } from '~/core/helpers/api-error-parser';
import { AuthenticationService } from '~/core/services/authentication.service';
import { OnlineCreditService } from '~/core/services/online-credit.service';
import { PrintingService } from '~/core/services/printing.service';
import { FormValidations } from '~/core/helpers/form-validations';
import ErrorParser from '~/core/helpers/ErrorParser';
import { OnlineCredit } from '~/core/models/online-credit';
import { CurrencyCasePipe } from '~/core/helpers/currency-case-pipe/currency-case-pipe';
import { UtilsService } from '~/core/helpers/utils';

@Component({
  selector: 'app-online-credit-dialog',
  imports: [
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatFormFieldModule,
    MatInputModule,
    MatButton,
    NgxMaskPipe,
    TranslatePipe,
    TitleCasePipe,
    DatePipe,
    CurrencyCasePipe,
  ],
  templateUrl: './online-credit-dialog.component.html',
  styleUrls: ['./online-credit-dialog.component.scss'],
})
export class OnlineCreditDialogComponent implements OnInit {
  dialogRef = inject<MatDialogRef<OnlineCreditDialogComponent>>(MatDialogRef);
  creditService = inject(OnlineCreditService);
  fb = inject(UntypedFormBuilder);
  dialog = inject(MatDialog);
  themeService = inject(ThemeService);
  auth = inject(AuthenticationService);
  printingService = inject(PrintingService);
  translate = inject(TranslateService);

  form: UntypedFormGroup;
  credit: OnlineCredit;
  showDetails = false;

  constructor() {}

  ngOnInit() {
    this.initForm();
  }

  initForm() {
    if (this.auth.isLoggedIn()) {
      this.form = this.fb.group({
        value: this.fb.control(null, [Validators.required, Validators.minLength(1)]),
        pin: this.fb.control(null, [Validators.required, Validators.minLength(6), Validators.pattern('[0-9]+')]),
        confirm_pin: this.fb.control(null, [
          Validators.required,
          Validators.minLength(6),
          Validators.pattern('[0-9]+'),
          FormValidations.matchPin('pin'),
        ]),
      });
    }

    if (this.credit) {
      this.showDetails = true;
    }
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  get currentTheme() {
    return this.themeService.getTheme();
  }

  search() {
    if (this.form.valid) {
      const data = {
        value: this.form.get('value').value,
        pin: this.form.get('pin').value,
      } as any;
      this.creditService.generate(data).subscribe(
        (result) => {
          this.credit = result;
          this.showDetails = true;
          this.auth.refreshBalance();
        },
        (error) => {
          this.onNoClick();
          this.showError(error.error);
        },
      );
    }
  }

  hasError(formControlName: string): boolean {
    return this.form.get(formControlName).invalid;
  }

  getErrorMessage(formControlName: string) {
    const { errors } = this.form.get(formControlName);
    return ErrorParser(errors);
  }

  printCredit() {
    this.printingService.printCredit(this.credit);
  }

  showError(error) {
    const instance = this.dialog.open(MessageDialogComponent, { disableClose: true });
    instance.componentInstance.title = 'GENERAL.ONLINE_CREDIT.TITLE';
    instance.componentInstance.message = ApiErrorParser._parseCode(error.code);
  }

  get generatedRoute(): string {
    if (!UtilsService.isBrowser()) return null;
    return `${window.location.origin}?onlineCredit=${this.credit._id}`;
  }

  // whatsappUrl() {
  //   const url = `${window.location.origin}/home/event-area?coupon=code:${this.bet.code},pin:${this.getPINCode()}`;
  //   return url;
  // }

  shareInWhatsapp() {
    const whatsappUrl = `https://api.whatsapp.com/send?text=${
      this.translate.instant('GENERAL.ONLINE_CREDIT.WHATSAPP_MSG') + '%0a%0a' + this.generatedRoute
    }`;
    if (!UtilsService.isBrowser()) return;
    window.open(whatsappUrl);
  }
}
